import {autorun, makeAutoObservable, runInAction} from "mobx";
import {safeLocalStorage} from "../utils/safeLocalStorage";
import {autoOccupy} from "./autoOccupy";
import {connection} from "./connection";
import {OccupancyResponse} from "./types";
import {uiModel} from "./ui";

const OccupanciesStorageKey = "occupancies";

function createOccupancyMap(occupancies: OccupancyResponse[]) {
	const map = new Map<number, OccupancyResponse>();
	for (let occupancy of occupancies) {
		map.set(occupancy.id, occupancy);
	}
	return map;
}

class OccupancyModel {
	private occupancyMap = new Map<number, OccupancyResponse>();

	constructor() {
		makeAutoObservable<OccupancyModel, "occupancyMap">(this, {
			occupancyMap: true,
		});

		this.occupancyMap = createOccupancyMap(
			safeLocalStorage.readJson<OccupancyResponse[]>(OccupanciesStorageKey) || [],
		);

		// automatically save occupancies to storage on change
		autorun(() => {
			safeLocalStorage.writeJson(OccupanciesStorageKey, [...this.occupancyMap.values()]);
			console.info("Occupancies saved to localStorage.");
		});

		connection.addEventListener({
			onOccupancyUpdate: occupancy => runInAction(() => this.occupancyMap.set(occupancy.id, occupancy)),
			onOccupancyCreated: occupancy =>
				runInAction(() => {
					this.occupancyMap.set(occupancy.id, occupancy);
					uiModel.currentOccupancy = occupancy;
					if (autoOccupy.pending) {
						autoOccupy.pending = false;
					}
				}),
		});
	}

	getOccupancy(id: number | undefined) {
		return id ? this.occupancyMap.get(id) : undefined;
	}

	get occupancies() {
		return [...this.occupancyMap.values()];
	}

	hasOccupancyAtCube(hostname: string) {
		return !!this.occupancies.filter(occupancy => occupancy.box.cube.hostname == hostname).pop();
	}

	async fetch() {
		try {
			this.occupancyMap = createOccupancyMap(await connection.fetchOccupancies());
		}
		catch (error) {
			console.error("Error while fetching occupancies", error);
			throw error;
		}
	}

	async occupy(hostname: string) {
		try {
			await connection.occupy(hostname);
		}
		catch (error) {
			console.error("Error while occupying locker", error);
			throw error;
		}
	}

	async release(occupancy: OccupancyResponse) {
		try {
			await connection.release(occupancy.id, false);
		}
		catch (error) {
			console.error("Error while releasing locker", error);
			throw error;
		}
	}

	async changeLocker(occupancy: OccupancyResponse, boxSize?: string) {
		try {
			await connection.occupy(occupancy.box.cube.hostname, boxSize);
			await connection.release(occupancy.id, true);
			this.occupancyMap.delete(occupancy.id);
		}
		catch (error) {
			console.error("Error while changing locker", error);
			throw error;
		}
	}
}

export const occupancyModel = new OccupancyModel();
